import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Space,
  Input,
  Select,
  DatePicker,
  Button,
  message,
  Tooltip,
  Empty,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { fetchAdminOperationLogs, fetchAdminOperationLogStats } from '../../api/operationLog';

const { RangePicker } = DatePicker;
const { Option } = Select;

const statusMap = {
  1: { text: '成功', color: 'green', icon: <CheckCircleOutlined /> },
  0: { text: '失败', color: 'red', icon: <CloseCircleOutlined /> },
};

const AuditLogPage = () => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, successCount: 0, failureCount: 0, byAction: [], byResource: [] });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [action, setAction] = useState('');
  const [resource, setResource] = useState('');
  const [username, setUsername] = useState('');
  const [keyword, setKeyword] = useState('');
  const [dateRange, setDateRange] = useState(null);

  const loadData = async (nextPage = page, nextLimit = limit) => {
    setLoading(true);
    try {
      const params = {
        page: nextPage,
        limit: nextLimit,
        status,
        action,
        resource,
        username,
        keyword,
      };
      if (dateRange && dateRange.length === 2) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }

      const [logsRes, statsRes] = await Promise.all([
        fetchAdminOperationLogs(params),
        fetchAdminOperationLogStats(
          dateRange && dateRange.length === 2
            ? {
                startDate: dateRange[0].format('YYYY-MM-DD'),
                endDate: dateRange[1].format('YYYY-MM-DD'),
              }
            : {}
        ),
      ]);

      if (logsRes.success) {
        setLogs(logsRes.data.list || []);
        setTotal(logsRes.data.total || 0);
      }
      if (statsRes.success) {
        setStats(statsRes.data || { total: 0, successCount: 0, failureCount: 0, byAction: [], byResource: [] });
      }
    } catch (error) {
      message.error('加载审计日志失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(page, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, status, action, resource, username, keyword, dateRange]);

  const actionOptions = useMemo(() => {
    const set = new Set();
    stats.byAction?.forEach((item) => item.action && set.add(item.action));
    logs.forEach((item) => item.action && set.add(item.action));
    return Array.from(set);
  }, [stats.byAction, logs]);

  const resourceOptions = useMemo(() => {
    const set = new Set();
    stats.byResource?.forEach((item) => item.resource && set.add(item.resource));
    logs.forEach((item) => item.resource && set.add(item.resource));
    return Array.from(set);
  }, [stats.byResource, logs]);

  const columns = [
    {
      title: '管理员',
      dataIndex: 'username',
      width: 120,
      render: (text, record) => text || record.adminUser?.username || '-',
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 140,
      render: (text) => <Tag color="blue">{text || '-'}</Tag>,
    },
    {
      title: '资源',
      dataIndex: 'resource',
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: '资源ID',
      dataIndex: 'resource_id',
      width: 100,
      render: (text) => text || '-',
    },
    {
      title: '描述',
      dataIndex: 'description',
      ellipsis: true,
      render: (text) => <Tooltip title={text}>{text || '-'}</Tooltip>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value) => <Tag color={statusMap[value]?.color || 'default'}>{statusMap[value]?.text || value}</Tag>,
    },
    {
      title: 'IP',
      dataIndex: 'ip_address',
      width: 140,
      render: (text) => text || '-',
    },
    {
      title: '耗时',
      dataIndex: 'execution_time',
      width: 100,
      render: (text) => (text ? `${text} ms` : '-'),
    },
    {
      title: '时间',
      dataIndex: 'created_at',
      width: 180,
      render: (text) => (text ? new Date(text).toLocaleString() : '-'),
    },
  ];

  return (
    <div className="workspace-page">
      <div className="workspace-header">
        <div>
          <h1 className="workspace-title">管理员操作审计</h1>
          <p className="workspace-description">记录管理员关键操作，支持追踪与回溯</p>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => loadData(page, limit)}>刷新</Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} className="workspace-summary">
        <Col span={6}><Card><Statistic title="总日志数" value={stats.total || 0} /></Card></Col>
        <Col span={6}><Card><Statistic title="成功操作" value={stats.successCount || 0} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col span={6}><Card><Statistic title="失败操作" value={stats.failureCount || 0} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
        <Col span={6}><Card><Statistic title="成功率" value={stats.successRate || 0} suffix="%" precision={2} valueStyle={{ color: '#1890ff' }} /></Card></Col>
      </Row>

      <Card style={{ marginTop: 16 }}>
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Space wrap>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="搜索关键词"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              style={{ width: 220 }}
            />
            <Input
              allowClear
              placeholder="管理员用户名"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={{ width: 180 }}
            />
            <Select value={status} onChange={setStatus} allowClear placeholder="状态" style={{ width: 120 }}>
              <Option value={1}>成功</Option>
              <Option value={0}>失败</Option>
            </Select>
            <Select value={action} onChange={setAction} allowClear placeholder="操作类型" style={{ width: 140 }}>
              {actionOptions.map((item) => (
                <Option key={item} value={item}>{item}</Option>
              ))}
            </Select>
            <Select value={resource} onChange={setResource} allowClear placeholder="资源" style={{ width: 140 }}>
              {resourceOptions.map((item) => (
                <Option key={item} value={item}>{item}</Option>
              ))}
            </Select>
            <RangePicker value={dateRange} onChange={setDateRange} />
          </Space>
          <Button
            onClick={() => {
              setStatus('');
              setAction('');
              setResource('');
              setUsername('');
              setKeyword('');
              setDateRange(null);
              setPage(1);
              loadData(1, limit);
            }}
          >
            重置
          </Button>
        </Space>

        <Table
          rowKey="id"
          loading={loading}
          dataSource={logs}
          columns={columns}
          scroll={{ x: 1400 }}
          locale={{ emptyText: <Empty description="暂无审计日志" /> }}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            onChange: (nextPage, nextLimit) => {
              setPage(nextPage);
              setLimit(nextLimit);
            },
          }}
        />
      </Card>
    </div>
  );
};

export default AuditLogPage;
